import React from "react";
import {
  Box,
  Typography,
  Chip,
  TextField,
  Stack,
  Tooltip,
  IconButton,
} from "@mui/material";
import RotateRightIcon from "@mui/icons-material/RotateRight";
import AspectRatioIcon from "@mui/icons-material/AspectRatio";
import { CropRect } from "./VideoCropOverlay";

interface VideoCropControlsProps {
  cropRect: CropRect;
  setCropRect: (crop: CropRect) => void;
  videoWidth: number;
  videoHeight: number;
  disabled?: boolean;
}

const COMMON_ASPECT_RATIOS = [
  { name: "16:9", value: 16 / 9 },
  { name: "4:3", value: 4 / 3 },
  { name: "1:1", value: 1 },
  { name: "9:16", value: 9 / 16 },
];

const VideoCropControls: React.FC<VideoCropControlsProps> = ({
  cropRect,
  setCropRect,
  videoWidth,
  videoHeight,
  disabled = false,
}) => {
  const handleInputChange = (field: keyof CropRect, value: string) => {
    if (disabled) return;

    const numValue = parseInt(value, 10);
    if (isNaN(numValue)) return;

    const newRect = { ...cropRect };

    switch (field) {
      case "x":
        newRect.x = Math.max(
          0,
          Math.min(numValue, videoWidth - cropRect.width),
        );
        break;
      case "y":
        newRect.y = Math.max(
          0,
          Math.min(numValue, videoHeight - cropRect.height),
        );
        break;
      case "width":
        newRect.width = Math.max(
          10,
          Math.min(numValue, videoWidth - cropRect.x),
        );
        break;
      case "height":
        newRect.height = Math.max(
          10,
          Math.min(numValue, videoHeight - cropRect.y),
        );
        break;
    }

    setCropRect(newRect);
  };

  const applyAspectRatio = (ratio: number) => {
    if (disabled) return;

    const currentArea = cropRect.width * cropRect.height;

    let newWidth = Math.sqrt(currentArea * ratio);
    let newHeight = newWidth / ratio;

    newWidth = Math.min(newWidth, videoWidth - cropRect.x);
    newHeight = Math.min(newHeight, videoHeight - cropRect.y);

    if (newWidth / newHeight !== ratio) {
      if (newWidth / newHeight > ratio) {
        newWidth = newHeight * ratio;
      } else {
        newHeight = newWidth / ratio;
      }
    }

    const oldCenterX = cropRect.x + cropRect.width / 2;
    const oldCenterY = cropRect.y + cropRect.height / 2;

    let newX = oldCenterX - newWidth / 2;
    let newY = oldCenterY - newHeight / 2;

    newX = Math.max(0, Math.min(newX, videoWidth - newWidth));
    newY = Math.max(0, Math.min(newY, videoHeight - newHeight));

    setCropRect({
      x: newX,
      y: newY,
      width: newWidth,
      height: newHeight,
    });
  };

  const rotate90Degrees = () => {
    if (disabled) return;

    const oldCenterX = cropRect.x + cropRect.width / 2;
    const oldCenterY = cropRect.y + cropRect.height / 2;

    const newWidth = cropRect.height;
    const newHeight = cropRect.width;

    let newX = oldCenterX - newWidth / 2;
    let newY = oldCenterY - newHeight / 2;

    newX = Math.max(0, Math.min(newX, videoWidth - newWidth));
    newY = Math.max(0, Math.min(newY, videoHeight - newHeight));

    setCropRect({
      x: newX,
      y: newY,
      width: newWidth,
      height: newHeight,
    });
  };

  const currentAspectRatio = cropRect.width / cropRect.height;
  const currentAspectString =
    COMMON_ASPECT_RATIOS.find(
      (ar) => Math.abs(ar.value - currentAspectRatio) < 0.01,
    )?.name || `${cropRect.width.toFixed(0)}:${cropRect.height.toFixed(0)}`;

  if (disabled) return null;

  return (
    <Box sx={{ mt: 2 }}>
      <Stack
        direction="row"
        spacing={2}
        sx={{ mb: 2, maxWidth: "100%", overflowX: "auto", py: 1 }}
        alignItems="center"
      >
        <Stack direction="row" spacing={1}>
          <TextField
            label="X"
            size="small"
            value={Math.round(cropRect.x)}
            onChange={(e) => handleInputChange("x", e.target.value)}
            inputProps={{ min: 0, max: videoWidth, type: "number" }}
            sx={{ width: 70 }}
          />
          <TextField
            label="Y"
            size="small"
            value={Math.round(cropRect.y)}
            onChange={(e) => handleInputChange("y", e.target.value)}
            inputProps={{ min: 0, max: videoHeight, type: "number" }}
            sx={{ width: 70 }}
          />
          <TextField
            label="W"
            size="small"
            value={Math.round(cropRect.width)}
            onChange={(e) => handleInputChange("width", e.target.value)}
            inputProps={{ min: 10, max: videoWidth, type: "number" }}
            sx={{ width: 70 }}
          />
          <TextField
            label="H"
            size="small"
            value={Math.round(cropRect.height)}
            onChange={(e) => handleInputChange("height", e.target.value)}
            inputProps={{ min: 10, max: videoHeight, type: "number" }}
            sx={{ width: 70 }}
          />
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center">
          <Chip
            size="small"
            color="primary"
            variant="outlined"
            label={`${currentAspectString}`}
            icon={<AspectRatioIcon fontSize="small" />}
          />

          <Tooltip title="Rotate crop area 90°">
            <IconButton size="small" onClick={rotate90Degrees}>
              <RotateRightIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      <Typography variant="caption" sx={{ display: "block", mb: 1 }}>
        Aspect Ratio:
      </Typography>

      <Stack direction="row" spacing={1}>
        {COMMON_ASPECT_RATIOS.map((ratio) => (
          <Chip
            key={ratio.name}
            label={ratio.name}
            size="small"
            variant={
              Math.abs(currentAspectRatio - ratio.value) < 0.01
                ? "filled"
                : "outlined"
            }
            onClick={() => applyAspectRatio(ratio.value)}
            sx={{ minWidth: 45 }}
          />
        ))}
      </Stack>
    </Box>
  );
};

export default VideoCropControls;
